/**
 * Every line of funnel copy in one typed module, sourced from The Handler
 * Playbook v3 (Part 1 — The Product Spine) and the spy15 mockup. Content
 * rules that are load-bearing:
 *   - Door cards show TITLES ONLY — blind choice is the intrigue engine.
 *   - The seven-mission tease appears on the order screen (first time the
 *     visitor hears the number seven).
 *   - The stakes line sits above the difficulty envelopes, small.
 */

export const BRAND = "SECRET MISSIONS: OPERATIVE ASSESSMENT";

export const INTRO = {
  /** {referrer} is replaced with the key-bound giver name. */
  title: "{referrer} gave you a one-use key.",
  subtitle: "Let’s see if they are right about you.",
  question:
    "Have you ever had a feeling, deep in your soul, that you were meant to be doing something far more important - something that matters a lot more than what you have been doing?",
  yes: "Yes, I get that feeling a lot",
  no: "No",
} as const;

export const ORDER = {
  lines: [
    "There are more of us than you’d guess.",
    "But nobody just joins. You earn your way in if you’re good enough.",
  ],
  tease:
    "Most people never turn their real dream into something that really matters. There are exactly seven missions between you and finding out whether you’re most people.",
  lead: "Your first secret mission begins now:",
  accept: "I’m in.",
  decline: "This sounds too dangerous. I’m out.",
} as const;

export const DOORS = {
  heading: "You only get one door: Choose wisely",
  love: "Someone you love",
  stranger: "Someone you barely know",
  enter: "ENTER",
} as const;

export const HANDLERS = {
  heading: "Choose your handler.",
  subtitle:
    "This is the voice on the other end of your texts - your briefing, your backup, your way out.",
  loading: "DECRYPTING ROSTER…",
  error: "The roster won’t decrypt. The line is bad, not your clearance.",
  retry: "Try again",
} as const;

export const DIFFICULTY = {
  heading: "Choose Mission Difficulty",
  /** {handler} replaced with the chosen handler's name. */
  subtitle:
    "Then your messaging app will open so you can text your operation’s codename to {handler}.",
  stakes: "New recruits get one chance. This is yours.",
  tiers: {
    beginner: "STANDARD",
    challenging: "CHALLENGING",
    improbable: "MISSION IMPROBABLE",
  },
} as const;

export const DELIVERED = {
  title: "MISSION REQUESTED",
  /** {handler} replaced with the chosen handler's name. */
  line: "{handler} will be talking with you via text.",
  closing: "Watch your phone.",
  replay: "Run it again",
} as const;

export const EJECT = {
  closedTitle: "The door is closed.",
  closedLine: "That’s where most people’s story ends.",
  reconsider: "Reconsider",
  wait: {
    line: "If you’re still here when this reaches zero, we’ll know you’re serious.",
    caption: "staying is the task · (demo: shortened from 60s)",
    seconds: 20,
  },
  declare: {
    lines: [
      "Most people don’t come back. The ones who do tend to be the ones we’re actually looking for.",
      "Type the words, and we’ll know you mean them:",
    ],
    require: "I want to change the world I live in.",
    placeholder: "type it here",
    button: "I mean it",
    matched: "✓ we believe you",
    /** {require} replaced with the exact phrase. */
    unmatched: "type exactly: “{require}”",
  },
  prove: {
    lines: [
      "You said no. Prove us wrong.",
      "One thing, in the next two minutes: text someone you haven’t spoken to in a while. Tell them you were thinking about them. Nothing else.",
      "Come back when it’s done. We’ll take your word for it.",
    ],
    button: "Done.",
    hint: "You completed a real secret mission before giving your number. That was the point.",
  },
} as const;

export const FALLBACK = {
  notFound: {
    stage: "FILE NOT FOUND",
    title: "This corridor doesn’t exist.",
    line: "Whatever you were looking for was moved, shredded, or never issued.",
    action: "Return to the entrance",
  },
  error: {
    stage: "TRANSMISSION FAULT",
    title: "Something went wrong on our side.",
    line: "The equipment glitched. Your file is intact.",
    retry: "Try again",
    home: "Return to the entrance",
  },
} as const;

/** Tiny template helper — replaces {name} tokens. */
export function fill(
  template: string,
  vars: Record<string, string | undefined>,
): string {
  return template.replace(/\{(\w+)\}/g, (m, key: string) => vars[key] ?? m);
}

/** Arriving with no key link, no texted link and nothing on this browser:
 *  the way in is a key, or signing back in. */
export const ENTRY = {
  title: "Do you have a key?",
  body: "Every operative starts with a key, passed on by someone who believes in them. Scan yours and your first mission begins now.",
  yes: "Yes, scan my key",
  no: "No",
  /** top right: a returning operative signs in with their number */
  logIn: "Log in",
  noKeyTitle: "Every mission starts with a key.",
  noKeyBody:
    "Keys pass from operative to operative. Ask someone who's already on a mission for theirs, and when it's in your hands, scan it here. We'll be waiting.",
  haveKeyNow: "I have a key now",
  /** signed in with a number that is on no operative's file */
  unknownTitle: "We don't see you in our system.",
  unknownBody:
    "That number isn't on any operative's file yet. If someone has passed you their key, scan it to start your first mission.",
  haveKey: "I have a key",
  otherNumber: "Try a different number",
  back: "Back",
  scanTitle: "Scan your key",
  scanHint: "Point your camera at the code on the key.",
  /** a key was read: shown a moment before its page opens */
  scanDone: "Key found",
  scanDoneHint: "Unlocking your first mission…",
  notAKey: "That's not a Secret Missions key. Try again.",
  noCamera:
    "We couldn't open your camera. Open your phone's camera app and point it at the key instead.",
} as const;
